import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Inbox,
    Mail,
    RefreshCw,
    ShieldOff,
} from "lucide-react";
import api from "@/lib/api";
import { LoadingState } from "@/components/ui/States";

/**
 * Shadow Inbox — admin view of dry-run notification_events.
 *
 * Surface for John to preview every alert that *would* have been sent
 * before flipping live email on. Admin-only. Never sends a real email.
 *
 * Wording rules (Feb 2026):
 *   - Backup Gmail address shown here only because /admin/* is auth-walled.
 *   - The standing warning "Dry-run preview only. No real email was sent."
 *     stays visible on the page so testers don't mistake the inbox for a
 *     log of sent mail.
 */
const PAGE_SIZE = 50;

export default function ShadowInbox() {
    const [data, setData] = useState(null);
    const [facets, setFacets] = useState({ template_ids: [], delivery_statuses: [] });
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);

    // Filters
    const [templateId, setTemplateId] = useState("");
    const [recipient, setRecipient] = useState("");
    const [leadId, setLeadId] = useState("");
    const [deliveryStatus, setDeliveryStatus] = useState("");
    const [onlyDryRun, setOnlyDryRun] = useState(true);
    const [page, setPage] = useState(0);

    const load = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams({
            limit: String(PAGE_SIZE),
            skip: String(page * PAGE_SIZE),
            only_dry_run: String(onlyDryRun),
        });
        if (templateId) params.set("template_id", templateId);
        if (recipient) params.set("recipient", recipient);
        if (leadId) params.set("lead_id", leadId);
        if (deliveryStatus) params.set("delivery_status", deliveryStatus);
        api.get(`/admin/notifications/shadow-inbox?${params.toString()}`)
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, [templateId, recipient, leadId, deliveryStatus, onlyDryRun, page]);

    useEffect(() => {
        api.get("/admin/notifications/shadow-inbox/facets").then((r) => setFacets(r.data));
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) return <LoadingState label="Loading dry-run shadow inbox…" />;

    const events = data?.events || [];
    const total = data?.total || 0;
    const pageStart = page * PAGE_SIZE + 1;
    const pageEnd = Math.min(total, page * PAGE_SIZE + events.length);

    return (
        <div data-testid="shadow-inbox-page">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
                <h1 className="m-0 flex items-center gap-2">
                    <Inbox size={28} className="text-[var(--color-accent)]" />
                    Shadow Inbox
                </h1>
                <Link to="/admin/automation-events" className="a1-btn-ghost text-sm" data-testid="shadow-inbox-link-automation-log">
                    Automation Event Log →
                </Link>
            </div>
            <p className="text-sm text-[var(--color-text-muted)] mb-3 max-w-3xl">
                Captured copies of every admin alert and customer-bound email that would have been sent. Newest first.
            </p>

            {/* Standing dry-run warning */}
            <div
                className="a1-card mb-4 flex items-start gap-3"
                style={{
                    background: "rgba(255, 214, 95, 0.08)",
                    borderColor: "var(--color-warning)",
                }}
                data-testid="shadow-inbox-dryrun-banner"
            >
                <AlertTriangle size={20} className="text-[var(--color-warning)] shrink-0 mt-0.5" />
                <div>
                    <p className="font-bold text-sm mb-1">Dry-run preview only. No real email was sent.</p>
                    <p className="text-xs text-[var(--color-text-muted)] leading-relaxed">
                        While Dry-Run Mode is on, every row in <code>notification_events</code> is rewritten to the shadow inbox{data?.dry_run_shadow_recipient ? <> (<code>{data.dry_run_shadow_recipient}</code>)</> : null}. Primary alert recipient: <code>{data?.primary_recipient || "(not configured)"}</code>{data?.backup_recipient ? <> · Backup: <code>{data.backup_recipient}</code></> : null}.
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="a1-card mb-4" data-testid="shadow-inbox-filters">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                    <Field label="Template">
                        <select
                            className="a1-input"
                            value={templateId}
                            onChange={(e) => { setTemplateId(e.target.value); setPage(0); }}
                            data-testid="filter-template"
                        >
                            <option value="">All templates</option>
                            {facets.template_ids.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                    <Field label="Recipient contains">
                        <input
                            className="a1-input"
                            placeholder="info@ / +dryrun / gmail"
                            value={recipient}
                            onChange={(e) => { setRecipient(e.target.value); setPage(0); }}
                            data-testid="filter-recipient"
                        />
                    </Field>
                    <Field label="Lead ID">
                        <input
                            className="a1-input"
                            placeholder="lead UUID"
                            value={leadId}
                            onChange={(e) => { setLeadId(e.target.value); setPage(0); }}
                            data-testid="filter-lead-id"
                        />
                    </Field>
                    <Field label="Delivery status">
                        <select
                            className="a1-input"
                            value={deliveryStatus}
                            onChange={(e) => { setDeliveryStatus(e.target.value); setPage(0); }}
                            data-testid="filter-delivery-status"
                        >
                            <option value="">All statuses</option>
                            {facets.delivery_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Mode">
                        <label className="text-sm flex items-center gap-2 mt-1.5">
                            <input
                                type="checkbox"
                                checked={onlyDryRun}
                                onChange={(e) => { setOnlyDryRun(e.target.checked); setPage(0); }}
                                data-testid="filter-only-dry-run"
                            />
                            Dry-run only
                        </label>
                    </Field>
                </div>
                <div className="flex justify-between items-center mt-3 text-xs text-[var(--color-text-muted)]">
                    <span data-testid="shadow-inbox-pagination-info">
                        {total === 0 ? "0 events" : `Showing ${pageStart}–${pageEnd} of ${total}`}
                    </span>
                    <div className="flex items-center gap-1">
                        <button
                            className="a1-btn-ghost text-xs"
                            onClick={load}
                            data-testid="shadow-inbox-refresh"
                        >
                            <RefreshCw size={12} /> Refresh
                        </button>
                        <button
                            className="a1-btn-ghost text-xs"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            data-testid="shadow-inbox-prev"
                        >
                            <ChevronLeft size={12} />
                        </button>
                        <button
                            className="a1-btn-ghost text-xs"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={(page + 1) * PAGE_SIZE >= total}
                            data-testid="shadow-inbox-next"
                        >
                            <ChevronRight size={12} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Inbox list */}
            <div className="grid gap-2" data-testid="shadow-inbox-list">
                {events.length === 0 ? (
                    <div className="a1-card text-center py-10">
                        <Inbox size={32} className="mx-auto text-[var(--color-text-muted)] mb-2" />
                        <p className="text-sm text-[var(--color-text-muted)]">
                            No dry-run notifications captured for these filters yet. Trigger one from the Notification Routing card on <Link to="/admin/verification" className="underline">Verification</Link>.
                        </p>
                    </div>
                ) : (
                    events.map((ev) => (
                        <InboxRow
                            key={`${ev.sent_at}-${ev.template_id}-${ev.intended_recipient}`}
                            event={ev}
                            open={openId === ev.sent_at}
                            onToggle={() => setOpenId(openId === ev.sent_at ? null : ev.sent_at)}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="a1-label text-xs">{label}</label>
            {children}
        </div>
    );
}

function InboxRow({ event, open, onToggle }) {
    const realSent = event.was_real_email_sent;
    return (
        <div
            className="a1-card p-0 overflow-hidden"
            data-testid={`shadow-inbox-row-${event.sent_at}`}
        >
            <button
                onClick={onToggle}
                className="w-full text-left p-3 flex items-start gap-3 hover:bg-[var(--color-bg-alt)] transition-colors"
                data-testid={`shadow-inbox-toggle-${event.sent_at}`}
            >
                <Mail size={18} className="text-[var(--color-accent)] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-mono text-xs text-[var(--color-text-muted)]">{event.sent_at?.slice(0, 19).replace("T", " ")}</span>
                        <code className="text-xs px-1.5 py-0.5 rounded" style={{ background: "var(--color-bg-alt)" }}>{event.template_id}</code>
                        <DryRunBadge dryRun={event.dry_run} />
                        <RealSentBadge realSent={realSent} />
                        {event.delivery_status ? (
                            <span className="a1-badge text-xs" style={{ background: "rgba(40, 216, 224, 0.1)", color: "var(--color-accent)", border: "1px solid var(--color-accent)" }}>
                                {event.delivery_status}
                            </span>
                        ) : null}
                    </div>
                    <p className="text-sm font-semibold mb-1 truncate" data-testid={`shadow-inbox-subject-${event.sent_at}`}>
                        {event.subject || "(no subject)"}
                    </p>
                    <div className="text-xs text-[var(--color-text-muted)] flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>To primary: <code>{event.intended_recipient || "(none)"}</code></span>
                        {event.backup_recipient ? <span>· Backup: <code>{event.backup_recipient}</code></span> : null}
                        <span>· Captured at: <code>{event.recipient}</code></span>
                        {event.lead_id ? <span>· Lead: <code>{event.lead_id.slice(0, 8)}…</code></span> : null}
                    </div>
                </div>
                <span className="shrink-0 text-[var(--color-text-muted)]">
                    {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </span>
            </button>

            {open ? (
                <div
                    className="p-3 border-t text-xs grid gap-2"
                    style={{ borderColor: "var(--color-line)", background: "var(--color-bg-alt)" }}
                    data-testid={`shadow-inbox-detail-${event.sent_at}`}
                >
                    <DetailRow label="Full subject" value={event.subject || "(none)"} />
                    <DetailRow label="Channel" value={event.channel || "email"} />
                    <DetailRow label="Priority" value={event.priority || "normal"} />
                    <DetailRow label="Actor" value={event.actor || "system"} />
                    {event.lead_id ? (
                        <div className="flex gap-3 items-start">
                            <span className="uppercase tracking-[0.12em] text-[var(--color-text-muted)] shrink-0 min-w-[120px]">Lead</span>
                            <Link to={`/admin/leads/${event.lead_id}`} className="underline">{event.lead_id}</Link>
                        </div>
                    ) : null}
                    <div className="flex gap-3 items-start">
                        <span className="uppercase tracking-[0.12em] text-[var(--color-text-muted)] shrink-0 min-w-[120px]">Body preview</span>
                        <pre className="m-0 whitespace-pre-wrap break-words flex-1">{event.body_preview || "(empty)"}</pre>
                    </div>
                    {event.automation_event_ref ? (
                        <div className="flex gap-3 items-start" data-testid={`shadow-inbox-ae-ref-${event.sent_at}`}>
                            <span className="uppercase tracking-[0.12em] text-[var(--color-text-muted)] shrink-0 min-w-[120px]">Automation event</span>
                            <div className="flex-1">
                                <Link
                                    to={`/admin/automation-events?event_type=${encodeURIComponent(event.automation_event_ref.event_type || "")}`}
                                    className="underline"
                                >
                                    {event.automation_event_ref.event_type} · {event.automation_event_ref.mode} · {event.automation_event_ref.status}
                                </Link>
                                {event.automation_event_ref.payload ? (
                                    <pre className="m-0 mt-1 whitespace-pre-wrap break-words text-[var(--color-text-muted)]">
                                        {JSON.stringify(event.automation_event_ref.payload, null, 2)}
                                    </pre>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                    <div
                        className="mt-2 p-2 rounded-md flex items-center gap-2"
                        style={{
                            background: "rgba(255, 214, 95, 0.08)",
                            border: "1px solid var(--color-warning)",
                        }}
                    >
                        <ShieldOff size={14} className="text-[var(--color-warning)]" />
                        <span className="text-xs font-bold" style={{ color: "var(--color-warning)" }}>
                            was_real_email_sent: false — captured to shadow inbox
                        </span>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function DetailRow({ label, value }) {
    return (
        <div className="flex gap-3 items-start">
            <span className="uppercase tracking-[0.12em] text-[var(--color-text-muted)] shrink-0 min-w-[120px]">{label}</span>
            <code className="flex-1 break-all">{value}</code>
        </div>
    );
}

function DryRunBadge({ dryRun }) {
    if (!dryRun) return null;
    return (
        <span
            className="a1-badge text-xs"
            style={{
                background: "rgba(255, 214, 95, 0.15)",
                color: "var(--color-warning)",
                border: "1px solid var(--color-warning)",
            }}
            data-testid="shadow-inbox-dry-run-badge"
        >
            DRY-RUN
        </span>
    );
}

function RealSentBadge({ realSent }) {
    if (realSent) {
        return (
            <span
                className="a1-badge text-xs"
                style={{
                    background: "rgba(231, 119, 78, 0.15)",
                    color: "var(--color-storm)",
                    border: "1px solid var(--color-storm)",
                }}
            >
                REAL EMAIL SENT
            </span>
        );
    }
    return (
        <span
            className="a1-badge text-xs"
            style={{
                background: "rgba(40, 216, 224, 0.08)",
                color: "var(--color-accent)",
                border: "1px solid var(--color-accent)",
            }}
        >
            no real email sent
        </span>
    );
}
